import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import chalk from "chalk";
import { ProductReferenceCalculationSource } from "@/features/diagnostics/resources/constants.ts";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import { getCurrentProductReference } from "@/lib/current-product.ts";
import Logger from "@/lib/logger.ts";
import { productsSetAction, productsSetCurrentProductReferenceAction } from "@/redux/slices/products-slice.ts";
import {
    smartNumberChangeMappingAction,
    smartNumberChangeMultipleColoursAction,
    smartNumberChangeMultipleMappingAction,
    smartNumberSetDescriptionAction,
    smartNumberSetMappingAction,
} from "@/redux/slices/smart-number-slice.ts";
import type { AppDispatch, RootState } from "@/redux/store.ts";
import { typedObjectKeys } from "@/utils/types.ts";

const currentProductMiddleware = createListenerMiddleware();

/**
 * Finds the current product reference whenever the internal mapping of the smart-number changes
 */
currentProductMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    matcher: isAnyOf(
        productsSetAction,
        smartNumberSetDescriptionAction,
        smartNumberSetMappingAction,
        smartNumberChangeMappingAction,
        smartNumberChangeMultipleMappingAction,
        smartNumberChangeMultipleColoursAction,
    ),
    effect: (_, api) => {
        window.currentProduct = null; // Reset current product on each calculation

        const state = api.getState();

        const { mapping, description } = state.smartNumber;
        const { items: products, isInitialCheck } = state.products;

        // Do not perform any calculations when the smart number mapping is missing or when there are no products
        if (!mapping || description.length === 0 || Object.keys(products).length === 0) return;

        const currentProduct = getCurrentProductReference(
            products,
            mapping,
            description,
            ProductReferenceCalculationSource.CurrentProductMiddleware,
        );

        let reference = currentProduct?.[0] ?? null;
        let product = currentProduct?.[1] ?? null;

        if (!currentProduct && isInitialCheck) {
            const firstReference = typedObjectKeys(products)[0];
            if (!firstReference) {
                Logger.error("No product data to fall back to!");
                return;
            }

            Logger.warn("Falling back to the first item in my list, which is", chalk.cyan(firstReference));
            DiagnosticsStore.collectEventLog("warning", `Falling back to first available reference ${firstReference}`);

            DiagnosticsStore.collectProductReferenceCalculation({
                source: ProductReferenceCalculationSource.CurrentProductMiddlewareFallback,
                reference: firstReference,
                productFound: true,
            });

            reference = firstReference;
            product = products[firstReference];
        }

        window.currentProduct = product; // Attach current product so we can easily keep track of it

        api.dispatch(productsSetCurrentProductReferenceAction(reference));
    },
});

export default currentProductMiddleware.middleware;
